import React, { useEffect, useState } from 'react';
import './tooltip.css';
import { API_URL, postAPI, postAPIForm } from '../../../app/api';
import { store } from '../../../app/store';
import { ToastContainer, toast } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';
import { hasPermission, isAdmin } from '../../../store/hooks/Permission';

const STATUS_OPTIONS = ['Open', 'In Progress', 'Resolved', 'Closed'];
const PRIORITY_OPTIONS = ['Low', 'Medium', 'High', 'Critical'];

export default function List() {
  const store_data = JSON.parse(store());
  const [tickets, setTickets] = useState([]);
  const [canCreate, setCanCreate] = useState(false);
  const [selectedTicket, setSelectedTicket] = useState(null);
  const [updateForm, setUpdateForm] = useState({ email: '', subject: '', description: '', status: 'Open', priority: 'Medium', resolution_notes: '' });
  const [createForm, setCreateForm] = useState({ email: '', subject: '', description: '', priority: 'Medium', attachment: null });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [canView, setCanView] = useState(false);
  const [canEdit, setCanEdit] = useState(false);
  const [canDelete, setCanDelete] = useState(false);

  const fetchTickets = async () => {
    const payload = {
      org_id: store_data?.org_id,
      user: store_data,
    };

    const response = await postAPI('/support-ticket/list', payload);

    if (response?.status === 1) {
      setTickets(response?.data || []);
    } else {
      setTickets([]);
      toast.error(response?.error || response?.message || 'Failed to fetch support tickets.');
    }
  };

  useEffect(() => {
    const checkPermissions = async () => {
      if (isAdmin()) {
        setCanView(true);
        setCanCreate(true);
        setCanEdit(true);
        setCanDelete(true);
        fetchTickets();
      } else {
        const viewAllowed = await hasPermission('SUPPORTTICKETS', 'SupportTickets', 'VIEW');
        const createAllowed = await hasPermission('SUPPORTTICKETS', 'SupportTickets', 'CREATE');
        const editAllowed = await hasPermission('SUPPORTTICKETS', 'SupportTickets', 'EDIT');
        const deleteAllowed = await hasPermission('SUPPORTTICKETS', 'SupportTickets', 'DELETE');

        setCanView(viewAllowed);
        setCanCreate(createAllowed);
        setCanEdit(editAllowed);
        setCanDelete(deleteAllowed);

        if (viewAllowed) {
          fetchTickets();
        }
      }
    };

    checkPermissions();
  }, []);

  const openUpdateModal = (ticket) => {
    setSelectedTicket(ticket);
    setUpdateForm({
      email: ticket?.email || '',
      subject: ticket?.subject || '',
      description: ticket?.description || '',
      status: ticket?.status || 'Open',
      priority: ticket?.priority || 'Medium',
      resolution_notes: ticket?.resolution_notes || '',
    });
  };

  const closeUpdateModal = () => {
    setSelectedTicket(null);
    setUpdateForm({ email: '', subject: '', description: '', status: 'Open', priority: 'Medium', resolution_notes: '' });
    setCreateForm({ email: '', subject: '', description: '', priority: 'Medium', attachment: null });
    setIsSubmitting(false);
  };

  const handleUpdate = async () => {
    if (!selectedTicket?.ticket_number || isSubmitting) return;

    if (!updateForm.email?.trim() || !updateForm.subject?.trim() || !updateForm.description?.trim()) {
      toast.error('Email, subject, and description are required.');
      return;
    }

    setIsSubmitting(true);
    const response = await postAPI('/support-ticket/update', {
      ticket_number: selectedTicket.ticket_number,
      email: updateForm.email,
      subject: updateForm.subject,
      description: updateForm.description,
      status: updateForm.status,
      priority: updateForm.priority,
      resolution_notes: updateForm.resolution_notes,
      user: store_data,
    });

    if (response?.status === 1) {
      toast.success('Support ticket updated successfully.');
      closeUpdateModal();
      fetchTickets();
    } else {
      toast.error(response?.error || response?.message || 'Failed to update support ticket.');
      setIsSubmitting(false);
    }
  };

  const handleCreate = async () => {
    if (isSubmitting) return;

    if (!createForm.email?.trim() || !createForm.subject?.trim() || !createForm.description?.trim()) {
      toast.error('Email, subject, and description are required.');
      return;
    }

    setIsSubmitting(true);
    const formData = new FormData();
    formData.append('org_id', store_data?.org_id || '');
    formData.append('user_id', store_data?.user_unique_id || '');
    formData.append('email', createForm.email);
    formData.append('subject', createForm.subject);
    formData.append('description', createForm.description);
    formData.append('priority', createForm.priority);

    if (createForm.attachment) {
      formData.append('attachment', createForm.attachment);
    }

    const response = await postAPIForm('/support-ticket/create', formData);

    if (response?.status === 1) {
      toast.success('Support ticket created successfully.');
      closeUpdateModal();
      fetchTickets();
    } else {
      toast.error(response?.error || response?.message || 'Failed to create support ticket.');
      setIsSubmitting(false);
    }
  };

  const handleRemove = async (ticketNumber) => {
    if (!canDelete) {
      toast.error('You do not have permission to perform this action.');
      return;
    }

    const response = await postAPI('/support-ticket/remove', {
      ticket_number: ticketNumber,
      user: store_data,
    });

    if (response?.status === 1) {
      toast.success('Support ticket removed successfully.');
      fetchTickets();
    } else {
      toast.error(response?.error || response?.message || 'Failed to remove support ticket.');
    }
  };

  const getAttachmentUrl = (attachment) => {
    if (!attachment) return '';
    return `${API_URL}/uploads/support-tickets/${attachment}`;
  };

  return (
    <>
      <ToastContainer />
      <div className="d-flex justify-content-end p-3">
        {canCreate && (
          <button
            className="btn btn-primary"
            type="button"
            data-bs-toggle="modal"
            data-bs-target="#SupportTicketCreateModal"
            onClick={closeUpdateModal}
          >
            <i className="ti ti-plus me-1"></i>
            Add Support Ticket
          </button>
        )}
      </div>
      <table className="table card-table">
        <thead>
          <tr>
            <th>Ticket Number</th>
            <th>Email</th>
            <th>Subject</th>
            <th>Status</th>
            <th>Priority</th>
            <th>Created</th>
            <th>Resolved</th>
            <th style={{ width: '220px' }}>Actions</th>
          </tr>
        </thead>
        <tbody className="table-border-bottom-0">
          {!canView ? (
            <tr>
              <td colSpan="8" className="text-center text-danger py-4">
                You do not have permission to view this page.
              </td>
            </tr>
          ) : tickets.length > 0 ? (
            tickets.map((ticket) => (
              <tr key={ticket?.id || ticket?.ticket_number}>
                <td>{ticket?.ticket_number || '-'}</td>
                <td>{ticket?.email || '-'}</td>
                <td style={{ maxWidth: '260px' }}>
                  <div className="fw-semibold">{ticket?.subject || '-'}</div>
                  <small className="text-muted d-block" style={{ whiteSpace: 'pre-wrap' }}>{ticket?.description || '-'}</small>
                </td>
                <td>
                  <span className={`badge ${ticket?.status === 'Resolved' ? 'bg-label-success' : ticket?.status === 'Closed' ? 'bg-label-secondary' : ticket?.status === 'In Progress' ? 'bg-label-info' : 'bg-label-warning'}`}>
                    {ticket?.status || '-'}
                  </span>
                </td>
                <td>
                  <span className={`badge ${ticket?.priority === 'Critical' ? 'bg-label-danger' : ticket?.priority === 'High' ? 'bg-label-danger' : ticket?.priority === 'Medium' ? 'bg-label-warning' : 'bg-label-success'}`}>
                    {ticket?.priority || '-'}
                  </span>
                </td>
                <td>{ticket?.created_at ? new Date(ticket.created_at).toLocaleString() : '-'}</td>
                <td>{ticket?.resolved_at ? new Date(ticket.resolved_at).toLocaleString() : '-'}</td>
                <td>
                  {ticket?.attachment && (
                    <a
                      className="badge bg-label-dark me-1 custom-tooltip text-decoration-none"
                      href={getAttachmentUrl(ticket.attachment)}
                      target="_blank"
                      rel="noreferrer"
                    >
                      <i className="fa fa-paperclip"></i>
                      <span className="tooltip-text">Open Attachment</span>
                    </a>
                  )}

                  {canEdit && (
                    <span
                      className="badge bg-label-primary me-1 custom-tooltip"
                      data-bs-toggle="modal"
                      data-bs-target="#SupportTicketUpdateModal"
                      onClick={() => openUpdateModal(ticket)}
                    >
                      <i className="fa fa-pen"></i>
                      <span className="tooltip-text">Update Ticket</span>
                    </span>
                  )}

                  {canDelete && (
                    <span
                      className="badge bg-label-danger me-1 custom-tooltip"
                      onClick={() => {
                        if (window.confirm('Are you sure? This action will permanently remove this ticket.')) {
                          handleRemove(ticket?.ticket_number);
                        }
                      }}
                    >
                      <i className="fa fa-trash"></i>
                      <span className="tooltip-text">Delete Ticket</span>
                    </span>
                  )}
                </td>
              </tr>
            ))
          ) : (
            <tr>
              <td colSpan="8" className="text-center py-4">No support tickets found.</td>
            </tr>
          )}
        </tbody>
      </table>

      <div className="modal fade" id="SupportTicketUpdateModal" tabIndex="-1" aria-hidden="true">
        <div className="modal-dialog modal-lg modal-simple modal-edit-user">
          <div className="modal-content p-3 p-md-5">
            <div className="modal-body">
              <button type="button" className="btn-close" data-bs-dismiss="modal" aria-label="Close" onClick={closeUpdateModal}></button>
              <div className="text-center mb-4">
                <h3 className="mb-2">Update Support Ticket</h3>
                <p className="text-muted mb-0">Adjust ticket status, priority, and resolution notes.</p>
              </div>

              <div className="row g-3">
                <div className="col-12 col-md-6">
                  <label className="form-label">Ticket Number</label>
                  <input className="form-control" value={selectedTicket?.ticket_number || ''} disabled />
                </div>
                <div className="col-12 col-md-6">
                  <label className="form-label">Email</label>
                  <input className="form-control" value={selectedTicket?.email || ''} disabled />
                </div>
                <div className="col-12 col-md-6">
                  <label className="form-label">Status</label>
                  <select
                    className="form-select"
                    value={updateForm.status}
                    onChange={(e) => setUpdateForm({ ...updateForm, status: e.target.value })}
                  >
                    {STATUS_OPTIONS.map((option) => (
                      <option key={option} value={option}>{option}</option>
                    ))}
                  </select>
                </div>
                <div className="col-12 col-md-6">
                  <label className="form-label">Priority</label>
                  <select
                    className="form-select"
                    value={updateForm.priority}
                    onChange={(e) => setUpdateForm({ ...updateForm, priority: e.target.value })}
                  >
                    {PRIORITY_OPTIONS.map((option) => (
                      <option key={option} value={option}>{option}</option>
                    ))}
                  </select>
                </div>
                <div className="col-12">
                  <label className="form-label">Subject</label>
                  <input
                    className="form-control"
                    value={updateForm.subject}
                    onChange={(e) => setUpdateForm({ ...updateForm, subject: e.target.value })}
                    placeholder="Enter ticket subject"
                  />
                </div>
                <div className="col-12">
                  <label className="form-label">Description</label>
                  <textarea
                    className="form-control"
                    rows="5"
                    value={updateForm.description}
                    onChange={(e) => setUpdateForm({ ...updateForm, description: e.target.value })}
                    placeholder="Enter support issue description"
                  ></textarea>
                </div>
                <div className="col-12">
                  <label className="form-label">Resolution Notes</label>
                  <textarea
                    className="form-control"
                    rows="5"
                    value={updateForm.resolution_notes}
                    onChange={(e) => setUpdateForm({ ...updateForm, resolution_notes: e.target.value })}
                    placeholder="Add resolution or internal update notes"
                  ></textarea>
                </div>
                <div className="col-12 text-center mt-3">
                  <button type="button" className="btn btn-primary me-2" onClick={handleUpdate} disabled={isSubmitting}>
                    {isSubmitting ? 'Updating...' : 'Update Ticket'}
                  </button>
                  <button type="button" className="btn btn-label-secondary" data-bs-dismiss="modal" onClick={closeUpdateModal}>Cancel</button>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      <div className="modal fade" id="SupportTicketCreateModal" tabIndex="-1" aria-hidden="true">
        <div className="modal-dialog modal-lg modal-simple modal-edit-user">
          <div className="modal-content p-3 p-md-5">
            <div className="modal-body">
              <button type="button" className="btn-close" data-bs-dismiss="modal" aria-label="Close" onClick={closeUpdateModal}></button>
              <div className="text-center mb-4">
                <h3 className="mb-2">Create Support Ticket</h3>
                <p className="text-muted mb-0">Create a new support ticket from admin panel.</p>
              </div>

              <div className="row g-3">
                <div className="col-12 col-md-6">
                  <label className="form-label">Email</label>
                  <input
                    className="form-control"
                    value={createForm.email}
                    onChange={(e) => setCreateForm({ ...createForm, email: e.target.value })}
                    placeholder="Enter customer email"
                  />
                </div>
                <div className="col-12 col-md-6">
                  <label className="form-label">Priority</label>
                  <select
                    className="form-select"
                    value={createForm.priority}
                    onChange={(e) => setCreateForm({ ...createForm, priority: e.target.value })}
                  >
                    {PRIORITY_OPTIONS.map((option) => (
                      <option key={option} value={option}>{option}</option>
                    ))}
                  </select>
                </div>
                <div className="col-12">
                  <label className="form-label">Subject</label>
                  <input
                    className="form-control"
                    value={createForm.subject}
                    onChange={(e) => setCreateForm({ ...createForm, subject: e.target.value })}
                    placeholder="Enter ticket subject"
                  />
                </div>
                <div className="col-12">
                  <label className="form-label">Description</label>
                  <textarea
                    className="form-control"
                    rows="5"
                    value={createForm.description}
                    onChange={(e) => setCreateForm({ ...createForm, description: e.target.value })}
                    placeholder="Enter support issue description"
                  ></textarea>
                </div>
                <div className="col-12">
                  <label className="form-label">Attachment</label>
                  <input
                    className="form-control"
                    type="file"
                    onChange={(e) => setCreateForm({ ...createForm, attachment: e.target.files?.[0] || null })}
                  />
                </div>
                <div className="col-12 text-center mt-3">
                  <button type="button" className="btn btn-primary me-2" onClick={handleCreate} disabled={isSubmitting}>
                    {isSubmitting ? 'Creating...' : 'Create Ticket'}
                  </button>
                  <button type="button" className="btn btn-label-secondary" data-bs-dismiss="modal" onClick={closeUpdateModal}>Cancel</button>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </>
  );
}